import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, UserIdSchema } from './common.js';
/**
 * The architecture of a cloud account, as discovery found it (ADR 0005).
 *
 * Shape, not contents: nodes carry an allow-listed set of configuration attributes — never data,
 * secret values, code or environment variable values. Every edge records where it came from (the
 * API and the field), so each line on the map can be traced to the configuration that implies it.
 *
 * Names, tags and attribute values were written by whoever controls the customer account, so all
 * of it is untrusted text: displayed, never interpreted, and only ever handed to a model as
 * delimited data.
 */
export const CloudNodeTypeSchema = z.enum([
    /** A DNS name that points at something in the account (Route 53 alias or CNAME). */
    'DOMAIN',
    'CLOUDFRONT_DISTRIBUTION',
    'WAF_WEB_ACL',
    'API_GATEWAY_HTTP',
    'API_GATEWAY_REST',
    'COGNITO_USER_POOL',
    'LOAD_BALANCER',
    'TARGET_GROUP',
    'ECS_SERVICE',
    'LAMBDA_FUNCTION',
    'SQS_QUEUE',
    'SNS_TOPIC',
    'EVENTBRIDGE_RULE',
    'STATE_MACHINE',
    'DYNAMODB_TABLE',
    'RDS_DATABASE',
    'ELASTICACHE_CLUSTER',
    'S3_BUCKET',
    'SECRET',
    'IAM_ROLE',
    /** An origin or integration outside the account — named, never discovered further. */
    'EXTERNAL',
]);
/**
 * How two resources are related. Each kind is read from a specific piece of configuration; none
 * is inferred from resource types alone.
 */
export const CloudEdgeKindSchema = z.enum([
    /** Traffic flows from → to: DNS alias, CDN origin, listener/target, API integration. */
    'ROUTES_TO',
    /** `from` is protected by the web ACL `to`. */
    'PROTECTED_BY',
    /** `from` (an API or load balancer) authenticates callers against `to` (a user pool). */
    'AUTHENTICATES_WITH',
    /** `from` invokes `to`: event-source mapping, subscription, rule target. */
    'TRIGGERS',
    /** `from` (a workload) runs as role `to`. */
    'RUNS_AS',
    /** `from` (a role) is allowed by IAM to act on `to` — the blast radius if it is compromised. */
    'CAN_ACCESS',
    /** `from`'s configuration names `to` (an environment variable value matched, then discarded). */
    'REFERENCES',
    /**
     * `from` can open a connection to `to`: a security group on `to` admits a security group `from`
     * is in, on the port in the evidence.
     */
    'NETWORK_REACHABLE',
]);
export const EdgeEvidenceSchema = z.object({
    /** The AWS API the relationship was read from, e.g. `cloudfront:ListDistributions`. */
    api: z.string().max(100),
    /** The field within it, e.g. `Origins.Items[].DomainName`. */
    field: z.string().max(200),
    /** A short, non-secret qualifier: a route key, a port, an action summary. */
    detail: z.string().max(300).optional(),
});
export const CloudNodeSchema = z.object({
    /** The ARN, or `dns:{name}` / `external:{host}` for nodes that have none. */
    id: z.string().min(1).max(2048),
    type: CloudNodeTypeSchema,
    name: z.string().max(512),
    region: z.string().max(30),
    /** The CloudFormation stack the resource belongs to, when it was created by one. */
    stack: z.string().max(256).optional(),
    /** Reachable from the internet by configuration: a public endpoint, distribution or bucket. */
    public: z.boolean(),
    /** Allow-listed, bounded configuration facts. Never data, secrets or environment values. */
    attributes: z.record(z.string().max(64), z.union([z.string().max(512), z.number(), z.boolean()])),
    tags: z.record(z.string().max(128), z.string().max(256)),
});
export const CloudEdgeSchema = z.object({
    from: z.string().max(2048),
    to: z.string().max(2048),
    kind: CloudEdgeKindSchema,
    evidence: EdgeEvidenceSchema,
});
/** What each collector managed: how many resources, or why it could not. */
export const CoverageEntrySchema = z.object({
    service: z.string().max(60),
    region: z.string().max(30),
    count: z.number().int().nonnegative(),
    /** An AWS error *code* only (e.g. `AccessDenied`) — never a message, which can echo input. */
    error: z.string().max(100).optional(),
    truncated: z.boolean().default(false),
});
export const CloudGraphSchema = z.object({
    discoveryId: IdSchema,
    orgId: OrgIdSchema,
    connectionId: IdSchema,
    accountId: z.string().regex(/^\d{12}$/),
    regions: z.array(z.string().max(30)),
    discoveredAt: IsoDateTimeSchema,
    nodes: z.array(CloudNodeSchema),
    edges: z.array(CloudEdgeSchema),
    coverage: z.array(CoverageEntrySchema),
});
export const DiscoveryStatusSchema = z.enum(['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED']);
/** One discovery run. The graph itself is in the evidence bucket; this is its record. */
export const DiscoverySchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    connectionId: IdSchema,
    status: DiscoveryStatusSchema,
    requestedByUserId: UserIdSchema,
    requestedAt: IsoDateTimeSchema,
    finishedAt: IsoDateTimeSchema.optional(),
    nodeCount: z.number().int().nonnegative().optional(),
    edgeCount: z.number().int().nonnegative().optional(),
    /** SHA-256 of the stored graph's canonical JSON; re-checked on every read. */
    digest: z.string().length(64).optional(),
    /** Services that could not be read, by error code. */
    gaps: z.array(z.string().max(200)).max(200).default([]),
    error: z.string().max(300).nullish(),
});
export const ListDiscoveriesResponseSchema = z.object({ items: z.array(DiscoverySchema) });
/**
 * Why a resource is proposed as part of an application — evidence, not a verdict. The AI's
 * boundary proposal (phase 3) starts from these; a person decides.
 */
export const SeedReasonSchema = z.object({
    nodeId: z.string().max(2048),
    /** `ENTRYPOINT` — matches one of the application's hosts; `REACHABLE` — reached by an edge from
     * a seed resource; `SAME_STACK` — in a CloudFormation stack a seed resource belongs to. */
    kind: z.enum(['ENTRYPOINT', 'REACHABLE', 'SAME_STACK']),
    /** The host, edge kind or stack that explains it. */
    via: z.string().max(512),
    /** For REACHABLE and SAME_STACK: the seed resource it came from. */
    from: z.string().max(2048).optional(),
});
export const GraphChangesSchema = z.object({
    previousDiscoveryId: IdSchema,
    addedNodes: z.array(z.string().max(2048)),
    removedNodes: z.array(z.string().max(2048)),
});
/** GET /v1/applications/{id}/cloud-graph */
export const ApplicationCloudGraphSchema = z.object({
    discovery: DiscoverySchema,
    graph: CloudGraphSchema,
    /** The stored graph still hashes to the digest recorded when it was written. */
    integrity: z.enum(['verified', 'changed']),
    /** The application's hosts the seed started from. */
    entrypointHosts: z.array(z.string().max(253)),
    seed: z.array(SeedReasonSchema),
    changes: GraphChangesSchema.nullable(),
});
//# sourceMappingURL=cloud-graph.js.map